import React, { useEffect, useState } from 'react';
import {
  Star, Users, Award, Heart, Brain, Target, Phone,
  CheckCircle, Play, MapPin, Globe, MessageCircle, Sparkles,
  ArrowRight, Clock, BadgeCheck, Mail, Shield, BookOpen,
  GraduationCap, Video, ChevronRight, AlertCircle
} from 'lucide-react';
import { fetchPublicPsychologyVendor } from './humanPsychologyApi';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { SEO } from '../../SEO';

interface ApiInstructor {
  _id: string;
  mentorId: string;
  firstname: string;
  lastname: string;
  useremail: string;
  phone: string;
  specializations: string[];
  certification: string;
  bio: string;
  languages: string[];
  skill: string[];
  profileIcon?: string;
  profileBanner?: string;
}

interface Instructor {
  mentorId: string;
  name: string;
  email: string;
  specialty: string;
  experience: string;
  rating: number;
  reviews: number;
  price: string;
  languages: string[];
  image: string;
  profileBanner?: string;
  location?: string;
  phone?: string;
  profileQuote?: string;
  certification: string[];
  description: string;
  availability: string;
  skills: string[];
  slug: string;
}

const transformApiToInstructor = (apiData: ApiInstructor): Instructor => {

  // Parse skills from array or comma-separated string
  let skills: string[] = apiData.skill || [];
  if (skills.length === 1 && typeof skills[0] === 'string' && skills[0].includes(',')) {
    skills = skills[0].split(/,\s*/);
  }

  // Parse languages
  let languages: string[] = apiData.languages || [];
  if (languages.length === 1 && typeof languages[0] === 'string' && languages[0].includes(',')) {
    languages = languages[0].split(/,\s*/);
  }

  // Handle certification - it could be a string URL or array
  let certifications: string[] = [];
  if (apiData.certification) {
    if (Array.isArray(apiData.certification)) {
      certifications = apiData.certification;
    } else if (typeof apiData.certification === 'string') {
      certifications = [apiData.certification];
    }
  }

  // Generate a consistent rating based on name length (for demo)
  const rating = 4.0 + (apiData.firstname.length % 10) * 0.1;

  // Generate reviews count
  const reviews = 5 + Math.floor(Math.random() * 25);

  // Determine price based on available data
  const priceValue = "₹499/session";

  // Use profileIcon if available, otherwise fallback to UI Avatars
  const avatarUrl = apiData.profileIcon
    ? (apiData.profileIcon.startsWith("http") ? apiData.profileIcon : `${window.location.origin}${apiData.profileIcon.startsWith("/") ? "" : "/"}${apiData.profileIcon}`)
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(apiData.firstname + ' ' + apiData.lastname)}&background=6366f1&color=fff&size=200&bold=true`;

  return {
    mentorId: apiData.mentorId,
    name: `${apiData.firstname} ${apiData.lastname}`,
    email: apiData.useremail,
    specialty: apiData.bio || 'Human Psychology Specialist',
    experience: 'Certified Psychologist',
    rating: Math.min(5, Math.max(3.5, rating)), // Keep between 3.5 and 5
    reviews: reviews,
    price: priceValue,
    languages: languages.length > 0 ? languages : ['English'],
    image: avatarUrl,
    profileBanner: apiData.profileBanner,
    phone: apiData.phone,
    certification: certifications.length > 0 ? certifications : ['Professional Certification'],
    description: apiData.bio || 'Experienced psychology specialist dedicated to your mental wellness journey.',
    availability: 'Available',
    skills: skills.length > 0 ? skills : ['Psychology', 'Counseling', 'Mental Health'],
    slug: apiData.mentorId,
  };
};

/* ───────── Stat counter colours ───────── */
const statColors = [
  { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-100' },
  { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-100' },
  { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-100' },
  { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-100' },
];

// Avatar Placeholder Component
const AvatarPlaceholder = ({ name }: { name: string }) => {
  const initials = name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="w-full h-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center">
      <span className="text-3xl font-bold text-white">{initials}</span>
    </div>
  );
};

export default function HumanPsychologyPage() {
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedBio, setExpandedBio] = useState<Record<string, boolean>>({});
  const [expandedSkills, setExpandedSkills] = useState<Record<string, boolean>>({});

  const toggleBio = (id: string) => {
    setExpandedBio(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleSkills = (id: string) => {
    setExpandedSkills(prev => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    const fetchAndSetInstructors = async () => {
      try {
        setLoading(true);
        setError(null);

        const res: any = await fetchPublicPsychologyVendor();
        let apiData = null;

        if (res && res.success && Array.isArray(res.data)) {
          apiData = res.data;
        } else if (res && Array.isArray(res.data)) {
          apiData = res.data;
        } else if (res && Array.isArray(res)) {
          apiData = res;
        } else if (res && res.data && res.data.data && Array.isArray(res.data.data)) {
          apiData = res.data.data;
        } else {
          console.error('❌ Unexpected response format:', res);
          setError('Unable to load specialists. Unexpected data format.');
          setLoading(false);
          return;
        }

        if (!apiData || apiData.length === 0) {
          console.warn('⚠️ No specialists found in response');
          setError('No psychology specialists available at the moment.');
          setLoading(false);
          return;
        }

        // Transform the data
        const transformedData = apiData.map((item: any, index: number) => {
          return transformApiToInstructor(item);
        });

        setInstructors(transformedData);
        setLoading(false);

      } catch (err: any) {
        console.error('❌ Error in fetchAndSetInstructors:', err);
        setError(`Error: ${err.message || 'Failed to fetch specialists'}`);
        setLoading(false);
      }
    };

    fetchAndSetInstructors();
    AOS.init({ duration: 800, once: true });
  }, []);

  const benefits = [
    'Personalized Counseling: Tailored support for your specific emotional needs',
    'Expert Guidance: Certified psychologists with practical experience',
    'Safe Space: Confidential and non-judgmental environment',
    'Practical Tools: Actionable exercises for mental well-being'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <SEO
        title="Human Psychology & Certified Counseling Specialists | MetaFit Wellness"
        description="Consult with certified human psychologists and counselors at MetaFit Wellness. Overcome emotional challenges, build resilience, and unlock your true potential."
        canonical="/human-psychology"
        keywords="psychologist, human psychology, mental counseling, emotional wellness, cognitive behavioral therapy, MetaFit Wellness"
      />

      {/* ═══════════ HERO ═══════════ */}
      <section className="relative overflow-hidden py-8 md:py-12">
        {/* decorative blobs */}
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-indigo-200/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold uppercase tracking-widest mb-6">
            <Sparkles className="w-4 h-4" /> Human Psychology
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 leading-tight mb-4">
            Understand Your Mind,<br />
            <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Transform Your Life
            </span>
          </h1>
          <p className="text-base md:text-lg text-slate-500 max-w-3xl mx-auto leading-relaxed">
            Connect with certified psychology specialists who help you manage emotions,
            build resilience, and unlock your full potential.
          </p>


        </div>
      </section>

      {/* ═══════════ SPECIALISTS GRID ═══════════ */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-slate-900">Our Specialists</h2>
              <p className="text-slate-500 mt-1">Handpicked experts for your wellness journey</p>
            </div>
            {!loading && !error && instructors.length > 0 && (
              <span className="hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-full text-sm font-semibold border border-indigo-100">
                <BadgeCheck className="w-4 h-4" /> {instructors.length} Available
              </span>
            )}
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="relative w-16 h-16">
                <div className="absolute inset-0 rounded-full border-4 border-indigo-100" />
                <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-indigo-600 animate-spin" />
              </div>
              <p className="mt-6 text-slate-500 font-medium">Loading specialists…</p>
            </div>
          ) : error ? (
            <div className="text-center py-24 rounded-3xl border-2 border-dashed border-slate-200 bg-slate-50/50">
              <AlertCircle className="w-14 h-14 text-amber-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700">{error}</h3>
              <p className="text-sm text-slate-400 mt-2">Please try again later or contact support.</p>
            </div>
          ) : instructors.length === 0 ? (
            <div className="text-center py-24 rounded-3xl border-2 border-dashed border-slate-200 bg-slate-50/50">
              <Users className="w-14 h-14 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700">No Specialists Available</h3>
              <p className="text-sm text-slate-400 mt-2">Check back soon for new specialists!</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                {instructors.map((inst, idx) => (
                  <div
                    key={inst.mentorId || idx}
                    data-aos="fade-up"
                    data-aos-delay={idx * 100}
                    className="group relative bg-white rounded-[28px] border border-slate-200/80 overflow-hidden shadow-sm hover:shadow-2xl hover:border-indigo-200 transition-all duration-500"
                  >
                    {/* Top gradient accent / Banner */}
                    <div className="h-32 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-400 relative overflow-hidden">
                      {inst.profileBanner && (
                        <img
                          src={inst.profileBanner.startsWith("http") ? inst.profileBanner : `${window.location.origin}${inst.profileBanner.startsWith("/") ? "" : "/"}${inst.profileBanner}`}
                          alt={`${inst.name} Banner`}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          onError={(e) => {
                            (e.target as HTMLElement).style.display = 'none';
                          }}
                        />
                      )}
                      <div className="absolute inset-0 opacity-20"
                        style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                      {/* Availability badge */}
                      <div className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md text-white text-xs font-semibold">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                        Available
                      </div>
                      {/* Price badge */}
                      <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md text-white text-xs font-bold">
                        {inst.price}
                      </div>
                    </div>

                    {/* Avatar – overlapping the gradient */}
                    <div className="relative -mt-16 px-6">
                      <div className="w-28 h-28 rounded-2xl border-4 border-white shadow-xl overflow-hidden bg-white">
                        {inst.image ? (
                          <img
                            src={inst.image}
                            alt={inst.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              // Show fallback
                              const parent = target.parentElement;
                              if (parent) {
                                parent.innerHTML = '';
                                parent.appendChild(document.createElement('div'));
                                const div = parent.firstChild as HTMLDivElement;
                                div.className = 'w-full h-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center';
                                div.innerHTML = `<span class="text-3xl font-bold text-white">${inst.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)}</span>`;
                              }
                            }}
                          />
                        ) : (
                          <AvatarPlaceholder name={inst.name} />
                        )}
                      </div>
                    </div>

                    {/* Body */}
                    <div className="px-6 pt-4 pb-6 flex flex-col min-h-[300px]">
                      {/* Name & rating */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-slate-900 line-clamp-1">{inst.name}</h3>
                          <p className="text-indigo-600 text-sm font-medium mt-1">{inst.specialty}</p>
                        </div>
                        <div className="flex items-center gap-1 bg-amber-50 px-2.5 py-1 rounded-lg flex-shrink-0 ml-2">
                          <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                          <span className="text-sm font-bold text-slate-800">{inst.rating.toFixed(1)}</span>
                          <span className="text-xs text-slate-400">({inst.reviews})</span>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="text-sm text-slate-500 mb-3 border-l-2 border-indigo-200 pl-3">
                        <p className={expandedBio[inst.mentorId] ? "" : "line-clamp-2"}>
                          {inst.description}
                        </p>
                        {inst.description && inst.description.length > 120 && (
                          <button
                            onClick={() => toggleBio(inst.mentorId)}
                            className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors mt-1 focus:outline-none"
                          >
                            {expandedBio[inst.mentorId] ? "Read Less" : "Read More"}
                          </button>
                        )}
                      </div>

                      {/* Skills */}
                      {inst.skills.length > 0 && (
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Expertise</p>
                            {inst.skills.length > 4 && (
                              <button
                                onClick={() => toggleSkills(inst.mentorId)}
                                className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 transition-colors focus:outline-none"
                              >
                                {expandedSkills[inst.mentorId] ? "Show Less" : "Show All"}
                              </button>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {(expandedSkills[inst.mentorId] ? inst.skills : inst.skills.slice(0, 4)).map((skill, i) => (
                              <span key={i} className="px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-[11px] font-semibold">
                                {skill}
                              </span>
                            ))}
                            {!expandedSkills[inst.mentorId] && inst.skills.length > 4 && (
                              <span className="px-2.5 py-1 bg-slate-100 text-slate-500 rounded-lg text-[11px] font-semibold">
                                +{inst.skills.length - 4}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Languages */}
                      <div className="flex items-center gap-2 text-xs text-slate-400 mb-4">
                        <Globe className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="line-clamp-1">{inst.languages.join(' • ') || 'English'}</span>
                      </div>

                      {/* Certifications */}
                      <div className="mb-4">
                        <div className="flex items-center gap-1 text-xs text-slate-400 mb-1">
                          <GraduationCap className="w-3.5 h-3.5" />
                          <span>Certified Professional</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-green-500" />
                          <span className="text-xs text-green-600 font-medium">Verified</span>
                        </div>
                      </div>

                      {/* CTA Buttons */}
                      <div className="mt-auto pt-4 border-t border-slate-100 flex items-center gap-3">
                        <Link
                          to={`/public/psycology-profile/${inst.mentorId}`}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl text-sm font-bold hover:shadow-lg hover:shadow-indigo-200 transition-all duration-300 group/btn"
                        >
                          View Profile
                          <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* ═══════════ BENEFITS ═══════════ */}
      <section className="py-16 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-[40px] p-8 md:p-12 shadow-sm border border-slate-100">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Emotional Wellness Matters</h2>
                <div className="space-y-4">
                  {benefits.map((benefit, i) => (
                    <div key={i} className="flex items-start gap-3" data-aos="fade-right" data-aos-delay={i * 100}>
                      <div className="mt-1 bg-indigo-100 p-1.5 rounded-full flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-indigo-600" />
                      </div>
                      <p className="text-slate-600">{benefit}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-indigo-50 p-6 rounded-3xl text-center" data-aos="zoom-in">
                  <Brain className="w-8 h-8 text-indigo-600 mx-auto mb-3" />
                  <h3 className="font-bold text-slate-900">Mindfulness</h3>
                  <p className="text-xs text-slate-500 mt-1">Stay present and focused</p>
                </div>
                <div className="bg-blue-50 p-6 rounded-3xl text-center" data-aos="zoom-in" data-aos-delay="100">
                  <Heart className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                  <h3 className="font-bold text-slate-900">Empathy</h3>
                  <p className="text-xs text-slate-500 mt-1">Understand self & others</p>
                </div>
                <div className="bg-purple-50 p-6 rounded-3xl text-center" data-aos="zoom-in" data-aos-delay="200">
                  <Shield className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                  <h3 className="font-bold text-slate-900">Resilience</h3>
                  <p className="text-xs text-slate-500 mt-1">Bounce back stronger</p>
                </div>
                <div className="bg-amber-50 p-6 rounded-3xl text-center" data-aos="zoom-in" data-aos-delay="300">
                  <Target className="w-8 h-8 text-amber-600 mx-auto mb-3" />
                  <h3 className="font-bold text-slate-900">Clarity</h3>
                  <p className="text-xs text-slate-500 mt-1">Clear goals for life</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


    </div>
  );
}